import {
  RequirementWorkflowType,
  WorkerRequirementMappingLevel,
} from "@clipboard-health/contract-worker-app-bff";
import { LoadingSpinner, Text } from "@clipboard-health/ui-components";
import { Box, Stack, type Theme } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useVirtuosoScrollRestoration } from "@src/appV2/lib/utils/useVirtuosoScrollRestoration";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { intersection } from "lodash";
import { useEffect, useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";

import { type WorkerRequirement } from "../api/types";
import { DocumentOverviewCard } from "../components/OverviewCard";
import { getCheckrRequirementStatus } from "../Details/Workflows/Checkr/getCheckrRequirementStatus";
import { useGetCheckrApplicationStatus } from "../Details/Workflows/Checkr/useGetCheckrApplicationStatus";
import {
  DOCUMENT_SEARCH_SCROLL_POSITION_KEY,
  WORKER_REQUIREMENT_STATUS_PILL_VARIANTS,
} from "../DocumentsPage/constants";
import { DOCUMENT_DETAILS_PATH, DOCUMENTS_BASE_PATH } from "../paths";
import { useGetWorkerLicensesAllowedForDocuments } from "../useGetWorkerLicensesAllowedForDocuments";
import { getApplicableWorkplacesDetails } from "../utils/getApplicableWorkplacesDetails";
import { isRequirementInstantReview } from "../utils/isRequirementInstantReview";
import { getCustomDocumentStatusCardContent } from "../utils/workerRequirementStatusContent";

type RequirementApplicability = "mandatory" | "workplace" | "optional";

const APPLICABILITY_LABELS: Record<RequirementApplicability, string | undefined> = {
  mandatory: "Mandatory",
  workplace: "Workplace specific",
  optional: undefined,
};

function getRequirementApplicability(requirement: WorkerRequirement): RequirementApplicability {
  const isRequiredForBooking = requirement.requirementMappings?.some(
    (mapping) => mapping.attributes.requiredForBooking
  );

  if (!isRequiredForBooking) {
    return "optional";
  }

  if (requirement.level === WorkerRequirementMappingLevel.HCF) {
    return "workplace";
  }

  return "mandatory";
}

interface DocumentSearchResultsProps {
  searchTerm: string;
  results: WorkerRequirement[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onEndReached: () => void;
  scrollParent: HTMLElement | undefined;
}

export function DocumentSearchResults(props: DocumentSearchResultsProps) {
  const { searchTerm, results, hasNextPage, isFetchingNextPage, onEndReached, scrollParent } =
    props;

  const worker = useDefinedWorker();
  const history = useHistory();
  const { licenses: availableLicenses } = useGetWorkerLicensesAllowedForDocuments();
  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  const { data: checkrApplicationStatus } = useGetCheckrApplicationStatus();

  const { virtuosoRef, restoreStateFrom, saveScrollPosition } = useVirtuosoScrollRestoration(
    DOCUMENT_SEARCH_SCROLL_POSITION_KEY,
    { saveOnUnmount: false }
  );

  // Reset scroll to top when search term changes, but not on initial mount
  // so that restored scroll position from navigation back is preserved.
  const prevSearchTermRef = useRef(searchTerm);
  useEffect(() => {
    const hasSearchTermChanged = prevSearchTermRef.current !== searchTerm;
    prevSearchTermRef.current = searchTerm;

    if (hasSearchTermChanged) {
      virtuosoRef.current?.scrollToIndex(0);
    }
  }, [searchTerm, virtuosoRef]);

  return (
    <Virtuoso
      ref={virtuosoRef}
      restoreStateFrom={restoreStateFrom}
      data={results}
      customScrollParent={scrollParent}
      endReached={() => {
        if (hasNextPage && !isFetchingNextPage) {
          onEndReached();
        }
      }}
      itemContent={(index, requirement) => {
        const requirementStatus =
          requirement.requirement?.attributes.requirementWorkflowType ===
          RequirementWorkflowType.CHECKR_BACKGROUND_CHECK
            ? getCheckrRequirementStatus({
                isRunBackgroundCheckOnBookingEnabled,
                status: requirement.status,
                isValid: requirement.isValid,
                checkrApplicationStatus: checkrApplicationStatus?.data,
              })
            : requirement.status;

        const documentStatusPillVariant =
          WORKER_REQUIREMENT_STATUS_PILL_VARIANTS[requirementStatus];

        const { pillLabel: documentStatusPillLabel } = getCustomDocumentStatusCardContent({
          isRunBackgroundCheckOnBookingEnabled,
          requirementWorkflowType: requirement.requirement?.attributes.requirementWorkflowType,
          status: requirementStatus,
        });

        const applicability = getRequirementApplicability(requirement);

        return (
          <Box pb={3}>
            <DocumentOverviewCard
              requirementName={requirement.requirement?.attributes.name ?? ""}
              documentStatusPillVariant={documentStatusPillVariant}
              documentStatusPillLabel={documentStatusPillLabel}
              applicabilityLabel={APPLICABILITY_LABELS[applicability]}
              qualifications={
                availableLicenses.length > 1
                  ? intersection(requirement.qualifications ?? [], availableLicenses)
                  : []
              }
              applicableWorkplacesDetails={
                requirement.level === WorkerRequirementMappingLevel.HCF
                  ? getApplicableWorkplacesDetails({
                      workerRequirement: requirement,
                      workerAddress: worker.address,
                    })
                  : undefined
              }
              isQuickSign={isRequirementInstantReview({
                workerRequirement: requirement,
              })}
              expiryDate={requirement.expiry ? parseISO(requirement.expiry) : undefined}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.DOCUMENT_SEARCH_RESULT_CLICKED, {
                  requirementId: requirement.requirementId,
                  resultPosition: index,
                });
                saveScrollPosition();
                history.push(
                  generatePath(
                    `${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}/${DOCUMENT_DETAILS_PATH}`,
                    {
                      requirementId: requirement.requirementId,
                    }
                  )
                );
              }}
            />
          </Box>
        );
      }}
      components={{
        Footer: () => {
          if (isFetchingNextPage) {
            return (
              <Stack alignItems="center" py={8}>
                <LoadingSpinner size="medium" variant="primary" />
                <Text mt={4} color={(theme: Theme) => theme.palette.text.secondary}>
                  Loading more documents...
                </Text>
              </Stack>
            );
          }

          return <Stack pb={6} />;
        },
      }}
    />
  );
}
